import { createLabelType, createLabel, createLabelTypeList } from '@/graphql/mutations'
import { labelTypeByNameAndGroup, labelsByGroupAndName } from '@/mixins/labelsQueries'
import { mixinMethods } from '@/main.js'

export const createDefaultTypes = async ( groupTenantId, api ) => {
    const defaultTypes = ['Associates', 'Vehicles']
    
    try{
        for( const type of defaultTypes ){
            const input = {
                group: groupTenantId,
                name: type
            }
            await api( createLabelType, {input} )
        }
    } catch( e ){
        console.error( e )
    }
}

/**
 * Copy labels from template to new tenant
 * Step 1: Copy label types (e.g., "Associates", "Vehicles")
 * Step 2: Copy labels (e.g., "Manager", "Truck")
 * Step 3: Link labels to their types
 */
export const copyLabelsFromTemplate = async (templateGroup, newTenantGroup) => {
    try {
        const { gLoadListAll, api } = mixinMethods;

        // Load all template and tenant data in parallel
        const [
            [templateLabelTypes, newTenantLabelTypes],
            [templateLabels, newTenantLabels]
        ] = await Promise.all([
            Promise.all([
                gLoadListAll(labelTypeByNameAndGroup, { group: templateGroup }, 'labelTypeByNameAndGroup'),
                gLoadListAll(labelTypeByNameAndGroup, { group: newTenantGroup }, 'labelTypeByNameAndGroup')
            ]),
            Promise.all([
                gLoadListAll(labelsByGroupAndName, { group: templateGroup }, 'labelsByGroupAndName'),
                gLoadListAll(labelsByGroupAndName, { group: newTenantGroup }, 'labelsByGroupAndName')
            ])
        ]);

        // STEP 1: Copy Label Types
        const typeMapping = await copyLabelTypes(
            templateLabelTypes,
            newTenantLabelTypes,
            newTenantGroup
        );

        // STEP 2: Copy Labels
        const { labelMapping, uniqueLabels } = await copyLabels(
            templateLabels,
            newTenantLabels,
            newTenantGroup
        );

        // STEP 3: Link labels to types
        await linkLabelsToTypes(
            uniqueLabels,
            labelMapping,
            typeMapping,
            newTenantGroup
        );

        return {
            success: true,
            templateLabelTypes: templateLabelTypes.length,
            templateLabels: uniqueLabels.size
        };

    } catch (error) {
        console.error('[Labels Template] ❌ FAILED:', error);
        throw error;
    }
}

// Helper: Copy label types and return mapping of old IDs to new IDs
const copyLabelTypes = async (templateTypes, existingTypes, tenantGroup) => {
    const { api } = mixinMethods;
    const existingTypesByName = new Map(existingTypes.map(t => [t.name, t.id]));
    const typeMapping = {};
    const createPromises = [];

    for (const templateType of templateTypes) {
        const existingId = existingTypesByName.get(templateType.name);
        
        if (existingId) {
            // Type already exists, just map it
            typeMapping[templateType.id] = existingId;
        } else {
            // Create new type
            createPromises.push(
                api(createLabelType, { input: { group: tenantGroup, name: templateType.name } })
                    .then(result => {
                        typeMapping[templateType.id] = result.data.createLabelType.id;
                    })
                    .catch(error => console.error(`[Labels Template] ✗ Failed to create type: ${templateType.name}`, error))
            );
        }
    }

    await Promise.all(createPromises);
    return typeMapping;
}

// Helper: Copy labels and return mapping of old IDs to new IDs
const copyLabels = async (templateLabels, existingLabels, tenantGroup) => {
    const { api } = mixinMethods;
    const existingLabelsByName = new Map(existingLabels.map(l => [l.name, l.id]));
    
    // Remove duplicates from template (keep first occurrence)
    const uniqueLabels = new Map();
    templateLabels.forEach(label => {
        if (!uniqueLabels.has(label.name)) {
            uniqueLabels.set(label.name, label);
        }
    });

    const labelMapping = {};
    const createPromises = [];

    for (const [name, templateLabel] of uniqueLabels) {
        const existingId = existingLabelsByName.get(name);
        
        if (existingId) {
            // Label already exists, just map it
            labelMapping[templateLabel.id] = existingId;
        } else {
            // Create new label
            createPromises.push(
                api(createLabel, {
                    input: { group: tenantGroup, name, status: templateLabel.status || true }
                })
                    .then(result => {
                        labelMapping[templateLabel.id] = result.data.createLabel.id;
                    })
                    .catch(error => console.error(`[Labels Template] ✗ Failed to create label: ${name}`, error))
            );
        }
    }

    await Promise.all(createPromises);
    return { labelMapping, uniqueLabels };
}

// Helper: Create associations between labels and types
const linkLabelsToTypes = async (uniqueLabels, labelMapping, typeMapping, tenantGroup) => {
    const { api } = mixinMethods;
    const linkPromises = [];

    for (const [, templateLabel] of uniqueLabels) {
        const newLabelId = labelMapping[templateLabel.id];
        if (!newLabelId) continue;

        const typeItems = templateLabel.typeList?.items || [];
        // Deduplicate type associations by type ID
        const uniqueTypeIds = new Set();
        for (const typeItem of typeItems) {
            const typeId = typeItem.type?.id;
            if (typeId) uniqueTypeIds.add(typeId);
        }

        for (const typeId of uniqueTypeIds) {
            const newTypeId = typeMapping[typeId];
            if (newTypeId) {
                linkPromises.push(
                    api(createLabelTypeList, {
                        input: {
                            labelTypeListLabelId: newLabelId,
                            labelTypeListTypeId: newTypeId,
                            group: tenantGroup
                        }
                    }).catch(error => console.error(`[Labels Template] ✗ Failed association: ${templateLabel.name} ↔ ${typeId}`, error))
                );
            }
        }
    }

    await Promise.all(linkPromises);
}